import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';
import {
  Bot,
  Send,
  Loader2,
  X,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Trophy,
  FileDown,
  ChevronRight,
  Target,
  BookOpen,
  Award,
  Brain,
  MessageCircle,
  ClipboardList,
  RefreshCw,
  Download,
  Maximize2,
  Minimize2,
  HelpCircle,
  LogOut,
  Zap,
  Clock,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
const API = `${API_BASE}/api`;

/**
 * Format NAVI response for clean display
 * Removes markdown asterisks and improves readability
 */
const formatNaviMessage = (content) => {
  if (!content) return content;
  
  // Remove markdown bold/italic asterisks
  let formatted = content.replace(/\*\*/g, '').replace(/\*/g, '');
  
  return formatted;
};

/**
 * Extract and highlight questions AND scenarios from NAVI's message
 */
const extractQuestionAndScenario = (content) => {
  if (!content) return { beforeQuestion: content, scenario: null, question: null, questionNumber: null, afterQuestion: null };
  
  let result = {
    beforeQuestion: content,
    scenario: null,
    question: null,
    questionNumber: null,
    afterQuestion: null
  };
  
  // First, try to find "Question X of Y: Topic" pattern
  const questionHeaderMatch = content.match(/Question\s+(\d+)\s+of\s+(\d+)[:\s]*([^\n]+)?/i);
  
  if (questionHeaderMatch) {
    const headerIndex = content.indexOf(questionHeaderMatch[0]);
    const afterHeader = content.substring(headerIndex + questionHeaderMatch[0].length).trim();
    
    result.beforeQuestion = content.substring(0, headerIndex).trim();
    result.questionNumber = `Question ${questionHeaderMatch[1]} of ${questionHeaderMatch[2]}`;
    
    // Check if there's a topic after the number
    const topic = questionHeaderMatch[3]?.trim();
    
    // Split remaining content into lines
    const remainingLines = afterHeader.split('\n').filter(l => l.trim());
    
    // Find the actual question (ends with ?)
    let scenarioLines = [];
    let questionText = null;
    let afterQuestionLines = [];
    let foundQuestion = false;
    
    for (let i = 0; i < remainingLines.length; i++) {
      const line = remainingLines[i].trim();
      
      if (!foundQuestion && line.endsWith('?')) {
        // This is the question
        questionText = line;
        foundQuestion = true;
        afterQuestionLines = remainingLines.slice(i + 1);
        break;
      } else if (!foundQuestion) {
        // This is scenario/context
        scenarioLines.push(line);
      }
    }
    
    // If no explicit question found, check if last line is the question
    if (!questionText && scenarioLines.length > 0) {
      const lastLine = scenarioLines[scenarioLines.length - 1];
      if (lastLine.endsWith('?')) {
        questionText = lastLine;
        scenarioLines = scenarioLines.slice(0, -1);
      }
    }
    
    result.scenario = scenarioLines.length > 0 ? scenarioLines.join('\n') : null;
    result.question = questionText;
    result.afterQuestion = afterQuestionLines.length > 0 ? afterQuestionLines.join('\n') : null;
    
    // Add topic to scenario if exists
    if (topic && result.scenario) {
      result.questionNumber = `${result.questionNumber}: ${topic}`;
    } else if (topic) {
      result.questionNumber = `${result.questionNumber}: ${topic}`;
    }
    
    return result;
  }
  
  // Fallback: Simple question patterns
  const simplePatterns = [
    /Here'?s? (?:your |the )?(?:next |first )?question[:\s]*\n?\s*(.+?\?)/i,
    /Question[:\s]+\n?\s*(.+?\?)/i,
  ];
  
  for (const pattern of simplePatterns) {
    const match = content.match(pattern);
    if (match) {
      const questionIndex = content.indexOf(match[0]);
      return {
        beforeQuestion: content.substring(0, questionIndex).trim(),
        scenario: null,
        question: match[1] || match[0],
        questionNumber: null,
        afterQuestion: content.substring(questionIndex + match[0].length).trim()
      };
    }
  }
  
  // Last resort: Check if message ends with a question
  const lines = content.split('\n').filter(l => l.trim());
  if (lines.length > 0) {
    const lastLine = lines[lines.length - 1];
    if (lastLine && lastLine.trim().endsWith('?') && lastLine.length > 20) {
      return {
        beforeQuestion: lines.slice(0, -1).join('\n'),
        scenario: null,
        question: lastLine,
        questionNumber: null,
        afterQuestion: null
      };
    }
  }
  
  return result;
};

/**
 * Render message content with proper formatting and question highlighting
 */
const MessageContent = ({ content, isNavi, highlightQuestions = true }) => {
  const formattedContent = isNavi ? formatNaviMessage(content) : content;
  
  // Extract question and scenario if this is a NAVI message
  const { beforeQuestion, scenario, question, questionNumber, afterQuestion } = isNavi && highlightQuestions 
    ? extractQuestionAndScenario(formattedContent) 
    : { beforeQuestion: formattedContent, scenario: null, question: null, questionNumber: null, afterQuestion: null };
  
  const renderParagraphs = (text) => {
    if (!text) return null;
    const paragraphs = text.split('\n').filter(p => p.trim());
    
    return paragraphs.map((paragraph, idx) => {
      // Check if it's a numbered list item
      const numberedMatch = paragraph.match(/^(\d+)\.\s(.+)/);
      if (numberedMatch) {
        return (
          <div key={idx} className="flex gap-2">
            <span className="text-navigate-gold font-semibold">{numberedMatch[1]}.</span>
            <span>{numberedMatch[2]}</span>
          </div>
        );
      }
      
      // Check if it's a bullet point
      if (paragraph.trim().startsWith('-') || paragraph.trim().startsWith('•')) {
        return (
          <div key={idx} className="flex gap-2 ml-2">
            <span className="text-[#1EBFB3]">•</span>
            <span>{paragraph.replace(/^[-•]\s*/, '')}</span>
          </div>
        );
      }
      
      // Check if it's a heading-like text (all caps or ends with :)
      if (paragraph === paragraph.toUpperCase() && paragraph.length < 50) {
        return (
          <h4 key={idx} className="font-semibold text-navigate-gold mt-3 mb-1">
            {paragraph}
          </h4>
        );
      }
      
      if (paragraph.endsWith(':') && paragraph.length < 60 && !paragraph.includes('?')) {
        return (
          <h4 key={idx} className="font-semibold text-white mt-2">
            {paragraph}
          </h4>
        );
      }
      
      // Regular paragraph
      return <p key={idx}>{paragraph}</p>;
    });
  };
  
  return (
    <div className="space-y-2">
      {renderParagraphs(beforeQuestion)}
      
      {/* Highlighted Question Box with Scenario */}
      {(questionNumber || scenario || question) && (
        <div className="my-4 p-4 bg-gradient-to-r from-navigate-gold/20 to-[#1EBFB3]/20 border-l-4 border-navigate-gold rounded-xl shadow-lg">
          {/* Question Number/Title */}
          {questionNumber && (
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-navigate-gold/30">
              <span className="text-navigate-gold font-bold text-base">
                {questionNumber}
              </span>
            </div>
          )}
          
          {/* Scenario/Context */}
          {scenario && (
            <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <p className="text-xs text-[#1EBFB3] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                Scenario
              </p>
              <p className="text-zinc-200 leading-relaxed italic">
                {scenario}
              </p>
            </div>
          )}
          
          {/* The Question */}
          {question && (
            <div className="flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-navigate-gold shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-navigate-gold font-semibold uppercase tracking-wider mb-1">
                  Your Question
                </p>
                <p className="text-white font-medium text-lg leading-relaxed">
                  {question}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {renderParagraphs(afterQuestion)}
    </div>
  );
};

/**
 * NaviAssessment Component
 * Enhanced assessment flow with:
 * - Admin-defined questions (0-10, optional)
 * - AI feedback on each response
 * - Pass/fail determination with XP awards
 * - Failed courses added to "Retake These Courses" dashboard
 * - Downloadable PDF summary
 */
export default function NaviAssessment({ 
  isOpen, 
  onClose, 
  courseId, 
  courseTitle,
  token,
  onComplete,
  userName = 'Learner',
  userJobTitle = 'Professional',
  defaultMode = ''
}) {
  const [assessmentMode, setAssessmentMode] = useState(null); // 'coach' or 'grader'
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [hasQuestions, setHasQuestions] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [passed, setPassed] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [completingCourse, setCompletingCourse] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Grader mode specific state
  const [graderAnswers, setGraderAnswers] = useState({});
  const [graderQuestions, setGraderQuestions] = useState([]);
  const [gradingInProgress, setGradingInProgress] = useState(false);
  const [graderResults, setGraderResults] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && !assessmentMode) {
      // Admin may force a default mode; otherwise show mode selection
      if (defaultMode === 'coach' || defaultMode === 'grader') {
        setAssessmentMode(defaultMode);
      }
      // else: leave null to show the learner's mode-selection screen
    } else if (isOpen && assessmentMode === 'coach' && !sessionId) {
      startAssessment();
    } else if (isOpen && assessmentMode === 'grader' && graderQuestions.length === 0) {
      loadGraderQuestions();
    }
  }, [isOpen, assessmentMode, defaultMode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !completed) {
      inputRef.current.focus();
    }
  }, [isOpen, messages, completed]);

  const startAssessment = async () => {
    setLoading(true);
    setCompleted(false);
    setPassed(false);
    setMessages([]);
    setSessionId(null);
    
    try {
      // Use the NAVI Coach endpoint for scenario-based coaching
      const response = await axios.post(
        `${API}/navi/coach/start`,
        { 
          course_id: courseId,
          user_name: userName,
          job_title: userJobTitle
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(response.data.session_id);
      setPhase(response.data.phase || 'scenario_setup');
      setTotalQuestions(response.data.total_questions || 4);
      setHasQuestions(true);
      setMessages([{
        role: 'navi',
        content: response.data.message,
        timestamp: new Date().toISOString(),
        type: 'scenario'
      }]);
    } catch (error) {
      console.error('Failed to start coaching session:', error);
      toast.error('Failed to start coaching session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load questions for Grader mode
  const loadGraderQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/navi/assessment/questions/${courseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.questions && response.data.questions.length > 0) {
        setGraderQuestions(response.data.questions);
        setTotalQuestions(response.data.questions.length);
        // Initialize answers object
        const initialAnswers = {};
        response.data.questions.forEach((q, idx) => {
          initialAnswers[idx] = '';
        });
        setGraderAnswers(initialAnswers);
      } else {
        toast.error('No assessment questions found for this course');
        setAssessmentMode(null);
      }
    } catch (error) {
      console.error('Failed to load grader questions:', error);
      toast.error('Failed to load assessment questions');
      setAssessmentMode(null);
    } finally {
      setLoading(false);
    }
  };

  // Submit answers for Grader mode
  const submitGraderAnswers = async () => {
    // Validate all answers are provided
    const answeredCount = Object.values(graderAnswers).filter(a => a && (typeof a === 'string' ? a.trim() : true)).length;
    if (answeredCount < totalQuestions) {
      toast.error(`Please answer all ${totalQuestions} questions before submitting`);
      return;
    }
    
    setGradingInProgress(true);
    try {
      const response = await axios.post(
        `${API}/navi/assessment/grade`,
        { 
          course_id: courseId,
          assessment_mode: assessmentMode, // "grader" for Foundational, "coach" for Intermediate
          answers: graderQuestions.map((q, idx) => {
            const question = typeof q === 'string' ? { type: 'open', question: q, options: [], correctAnswer: '' } : q;
            return {
              type: question.type || 'open',
              question: question.question || q,
              answer: graderAnswers[idx] || '',
              correctAnswer: question.correctAnswer || '',
              options: question.options || []
            };
          })
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGraderResults(response.data);
      setCompleted(true);
      setPassed(response.data.passed);
      setSessionId(response.data.session_id);
      
      if (onComplete) {
        onComplete({
          passed: response.data.passed,
          session_id: response.data.session_id
        });
      }
    } catch (error) {
      console.error('Failed to grade assessment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to grade assessment. Please try again.';
      toast.error(errorMsg);
    } finally {
      setGradingInProgress(false);
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim() || sending || !sessionId) return;
    
    const messageText = userInput.trim();
    setUserInput('');
    setSending(true);
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      // Use the NAVI Coach chat endpoint
      const response = await axios.post(
        `${API}/navi/coach/chat`,
        { session_id: sessionId, message: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add NAVI's response
      setMessages(prev => [...prev, {
        role: 'navi',
        content: response.data.message,
        timestamp: new Date().toISOString(),
        type: response.data.message_type
      }]);
      
      // Update state
      setPhase(response.data.phase || phase);
      setCurrentQuestion(response.data.current_question || currentQuestion);
      setTotalQuestions(response.data.total_questions || totalQuestions);
      
      if (response.data.session_completed) {
        setCompleted(true);
        // Use actual pass/fail from backend response if available, don't auto-pass
        const actualPassed = response.data.passed ?? false;
        setPassed(actualPassed);
        
        if (onComplete) {
          onComplete({
            passed: actualPassed,
            session_id: sessionId
          });
        }
      }
    } catch (error) {
      console.error('Assessment chat failed:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the failed user message
      setMessages(prev => prev.slice(0, -1));
      setUserInput(messageText);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const downloadPdf = async () => {
    if (!sessionId) return;
    
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/navi/assessment/summary/${sessionId}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessment_${courseTitle?.replace(/\s+/g, '_') || 'summary'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Certificate functionality removed per requirements

  const retakeAssessment = () => {
    setAssessmentMode(null); // Go back to mode selection
    setSessionId(null);
    setMessages([]);
    setPhase('intro');
    setCurrentQuestion(0);
    setCompleted(false);
    setPassed(false);
    setGraderAnswers({});
    setGraderQuestions([]);
    setGraderResults(null);
  };

  // Close and mark course as complete
  const closeAndComplete = async () => {
    setCompletingCourse(true);
    try {
      // Mark course as complete via API
      const response = await axios.post(
        `${API}/courses/${courseId}/complete`,
        { passed: passed },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        toast.success('Course Completed!', {
          description: response.data.cpd_points_earned > 0 
            ? `${response.data.cpd_points_earned} CPD points added to your tracker!`
            : 'Great work finishing this course!'
        });
        
        // Trigger onComplete callback with full data
        if (onComplete) {
          onComplete({
            passed,
            xp_earned: response.data.xp_earned || 0,
            needs_retake: response.data.needs_retake || false,
            completed: true
          });
        }
      }
      
      // Close the modal
      onClose();
      
    } catch (error) {
      console.error('Failed to complete course:', error);
      toast.error('Failed to mark course complete. Please try again.');
    } finally {
      setCompletingCourse(false);
    }
  };

  // Calculate progress
  const getProgress = () => {
    if (completed) return 100;
    if (!hasQuestions || totalQuestions === 0) return 50;
    if (phase === 'intro') return 5;
    if (phase === 'questions') {
      return 10 + (currentQuestion / totalQuestions) * 80;
    }
    if (phase === 'analyzing') return 95;
    return 50;
  };

  // Get phase info
  const getPhaseInfo = () => {
    switch (phase) {
      case 'intro':
        return { icon: MessageCircle, label: 'Introduction', color: 'text-blue-400' };
      case 'questions':
        return { icon: ClipboardList, label: `Question ${currentQuestion} of ${totalQuestions}`, color: 'text-[#1EBFB3]' };
      case 'analyzing':
        return { icon: Brain, label: 'Analyzing Responses', color: 'text-yellow-400' };
      case 'completed':
        return { icon: passed ? Trophy : Target, label: passed ? 'Competent' : 'Review Needed', color: passed ? 'text-green-400' : 'text-amber-400' };
      default:
        return { icon: Sparkles, label: 'Assessment', color: 'text-[#1EBFB3]' };
    }
  };

  if (!isOpen) return null;

  // Minimized view - just a floating bar
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl p-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-800 transition-colors"
             onClick={() => setIsMinimized(false)}>
          <img 
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png" 
            alt="NAVI" 
            className="w-10 h-10 rounded-lg object-cover"
          />
          <div className="flex-1">
            <p className="text-white font-medium text-sm">Assessment</p>
            <p className="text-xs text-zinc-400">Click to expand</p>
          </div>
          <div className="flex items-center gap-1">
            <Progress value={getProgress()} className="w-16 h-2" />
            <span className="text-xs text-zinc-500 ml-1">{Math.round(getProgress())}%</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-1 text-zinc-500 hover:text-white rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const phaseInfo = getPhaseInfo();
  const PhaseIcon = phaseInfo.icon;

  // Modal size classes based on fullscreen state
  const modalSizeClass = isFullscreen 
    ? 'w-full h-full max-w-none rounded-none' 
    : 'w-full max-w-2xl h-[85vh] rounded-2xl';

  return (
    <div className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center ${isFullscreen ? '' : 'p-4'}`} data-testid="navi-assessment-modal">
      <div className={`${modalSizeClass} bg-zinc-900 border border-zinc-700 flex flex-col shadow-2xl transition-all duration-300`}>
        {/* Header */}
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png" 
                alt="NAVI" 
                className="w-10 h-10 rounded-xl object-cover"
              />
              <div>
                <h2 className="text-lg font-bold text-white">Assessment</h2>
                <p className="text-sm text-zinc-400 truncate max-w-[200px] md:max-w-none">{courseTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Minimize Button */}
              <button
                onClick={() => setIsMinimized(true)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                title="Minimize"
              >
                <Minimize2 className="h-5 w-5" />
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
              
              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="close-assessment-btn"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className={`flex items-center gap-2 ${phaseInfo.color}`}>
                <PhaseIcon className="h-4 w-4" />
                {phaseInfo.label}
              </span>
              <span className="text-zinc-500">{Math.round(getProgress())}%</span>
            </div>
            <Progress value={getProgress()} className="h-2" />
          </div>
        </div>

        {/* Messages Area */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isFullscreen ? 'max-w-4xl mx-auto w-full' : ''}`} data-testid="assessment-messages">
          {/* MODE SELECTION SCREEN */}
          {!assessmentMode && !loading && (
            <div className="flex items-center justify-center h-full">
              <div className="max-w-lg w-full space-y-6">
                <div className="text-center mb-8">
                  <img 
                    src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png" 
                    alt="NAVI" 
                    className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4"
                  />
                  <h3 className="text-xl font-bold text-white mb-2">Choose Your Assessment Mode</h3>
                  <p className="text-zinc-400">Select how you&apos;d like NAVI to assess your understanding</p>
                </div>
                
                {/* Coach Mode Option - INTERMEDIATE */}
                <button
                  onClick={() => setAssessmentMode('coach')}
                  className="w-full p-6 bg-zinc-800/50 border border-zinc-700 rounded-xl hover:border-[#1EBFB3]/50 hover:bg-zinc-800 transition-all group text-left"
                  data-testid="mode-coach"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1EBFB3]/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#1EBFB3]/30 transition-colors">
                      <MessageCircle className="h-6 w-6 text-[#1EBFB3]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                        NAVI Coach Me
                        <span className="text-xs px-2 py-0.5 bg-[#1EBFB3]/20 text-[#1EBFB3] rounded-full">Interactive</span>
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Intermediate</span>
                      </h4>
                      <p className="text-sm text-zinc-400 mt-1">
                        NAVI will guide you through each question, analyze your responses, and provide 
                        personalized feedback and coaching along the way.
                      </p>
                      <div className="mt-2 p-2 bg-[#1EBFB3]/10 border border-[#1EBFB3]/20 rounded-lg">
                        <p className="text-xs text-[#1EBFB3] flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          Earns <span className="font-semibold">+500 XP</span> upon passing
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          10-15 minutes
                        </span>
                        <span className="flex items-center gap-1">
                          <Brain className="h-3 w-3" />
                          Guided learning
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
                
                {/* Grader Mode Option - FOUNDATIONAL */}
                <button
                  onClick={() => setAssessmentMode('grader')}
                  className="w-full p-6 bg-zinc-800/50 border border-zinc-700 rounded-xl hover:border-amber-500/50 hover:bg-zinc-800 transition-all group text-left"
                  data-testid="mode-grader"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/30 transition-colors">
                      <ClipboardList className="h-6 w-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                        NAVI Grader
                        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">Quick</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">Foundational</span>
                      </h4>
                      <p className="text-sm text-zinc-400 mt-1">
                        Answer all MCQ questions at your own pace, then NAVI will automatically grade 
                        your responses and determine if you&apos;re competent.
                      </p>
                      <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <p className="text-xs text-amber-300 flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          Earns <span className="font-semibold">+500 XP</span> upon passing
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          5-10 minutes
                        </span>
                        <span className="flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          Auto-graded
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
                
                {/* Info Banner */}
                <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-xl">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-zinc-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-zinc-400">
                      <p className="font-medium text-zinc-300 mb-1">Assessment Modes</p>
                      <p><span className="text-[#1EBFB3]">NAVI Coach Me</span>: Interactive scenario-based assessment with immediate feedback. Best for deep learning.</p>
                      <p className="mt-1"><span className="text-amber-400">NAVI Grader</span>: Quick multiple-choice assessment. Best for knowledge verification.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* GRADER MODE UI */}
          {assessmentMode === 'grader' && !completed && (
            <>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-navigate-gold mx-auto mb-3" />
                    <p className="text-zinc-400">Loading assessment questions...</p>
                  </div>
                </div>
              ) : gradingInProgress ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-navigate-gold mx-auto mb-3" />
                    <p className="text-zinc-400">NAVI is grading your responses...</p>
                    <p className="text-xs text-zinc-500 mt-2">This may take a moment</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-6 pb-20">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="h-5 w-5 text-amber-400" />
                      <div>
                        <p className="text-sm font-medium text-amber-400">Grader Mode</p>
                        <p className="text-xs text-zinc-400">Answer all {totalQuestions} questions, then submit for grading</p>
                      </div>
                    </div>
                  </div>
                  
                  {graderQuestions.map((q, idx) => {
                    // Handle both string format and object format
                    const question = typeof q === 'string' ? { type: 'open', question: q, options: [], correctAnswer: '' } : q;
                    const questionType = question.type || 'open';
                    const questionText = question.question || q;
                    
                    return (
                      <div key={idx} className={`bg-zinc-800/50 border rounded-xl p-5 ${
                        questionType === 'mcq' ? 'border-blue-500/30' :
                        questionType === 'truefalse' ? 'border-green-500/30' :
                        'border-zinc-700'
                      }`}>
                        <div className="flex items-start gap-3 mb-3">
                          <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold ${
                            questionType === 'mcq' ? 'bg-blue-500/20 text-blue-400' :
                            questionType === 'truefalse' ? 'bg-green-500/20 text-green-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                questionType === 'mcq' ? 'bg-blue-500/20 text-blue-400' :
                                questionType === 'truefalse' ? 'bg-green-500/20 text-green-400' :
                                'bg-[#1EBFB3]/20 text-[#1EBFB3]'
                              }`}>
                                {questionType === 'mcq' ? 'Multiple Choice' :
                                 questionType === 'truefalse' ? 'True/False' : 'Open-Ended'}
                              </span>
                            </div>
                            <p className="text-white font-medium leading-relaxed">{questionText}</p>
                          </div>
                        </div>
                        
                        {/* MCQ Options */}
                        {questionType === 'mcq' && question.options && (
                          <div className="space-y-2 ml-10">
                            {question.options.map((option, optIdx) => {
                              const letter = ['A', 'B', 'C', 'D'][optIdx];
                              // Store the actual option text, not just the letter
                              const isSelected = graderAnswers[idx] === option;
                              return (
                                <label 
                                  key={optIdx}
                                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-all ${
                                    isSelected 
                                      ? 'bg-blue-500/20 border-blue-500/50' 
                                      : 'bg-zinc-900/50 border-zinc-700 hover:border-zinc-600'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`mcq-${idx}`}
                                    checked={isSelected}
                                    onChange={() => setGraderAnswers(prev => ({ ...prev, [idx]: option }))}
                                    className="w-4 h-4 text-blue-500"
                                  />
                                  <span className="text-blue-400 font-bold">{letter}.</span>
                                  <span className="text-zinc-300">{option}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* True/False Options */}
                        {questionType === 'truefalse' && (
                          <div className="flex gap-4 ml-10">
                            <label 
                              className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg cursor-pointer border transition-all ${
                                graderAnswers[idx] === 'True'
                                  ? 'bg-green-500/20 border-green-500/50' 
                                  : 'bg-zinc-900/50 border-zinc-700 hover:border-zinc-600'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`tf-${idx}`}
                                checked={graderAnswers[idx] === 'True'}
                                onChange={() => setGraderAnswers(prev => ({ ...prev, [idx]: 'True' }))}
                                className="w-4 h-4 text-green-500"
                              />
                              <span className="text-green-400 font-semibold">True</span>
                            </label>
                            <label 
                              className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg cursor-pointer border transition-all ${
                                graderAnswers[idx] === 'False'
                                  ? 'bg-red-500/20 border-red-500/50' 
                                  : 'bg-zinc-900/50 border-zinc-700 hover:border-zinc-600'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`tf-${idx}`}
                                checked={graderAnswers[idx] === 'False'}
                                onChange={() => setGraderAnswers(prev => ({ ...prev, [idx]: 'False' }))}
                                className="w-4 h-4 text-red-500"
                              />
                              <span className="text-red-400 font-semibold">False</span>
                            </label>
                          </div>
                        )}
                        
                        {/* Open-Ended Textarea */}
                        {questionType === 'open' && (
                          <Textarea
                            value={graderAnswers[idx] || ''}
                            onChange={(e) => setGraderAnswers(prev => ({ ...prev, [idx]: e.target.value }))}
                            placeholder="Type your answer here..."
                            className="min-h-[100px] bg-zinc-900 border-zinc-600 focus:border-amber-500 ml-10"
                          />
                        )}
                        
                        <div className="flex justify-end mt-2">
                          <span className={`text-xs ${graderAnswers[idx]?.trim?.() || graderAnswers[idx] ? 'text-green-400' : 'text-zinc-500'}`}>
                            {graderAnswers[idx]?.trim?.() || graderAnswers[idx] ? '✓ Answered' : 'Not answered'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Spacer for sticky footer */}
                  <div className="h-24"></div>
                </div>
                
                {/* Sticky footer for submit button - positioned at bottom of scrollable area */}
                <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-700 p-4 -mx-4 mt-4">
                  <div className="flex items-center justify-between px-4">
                    <div className="text-sm text-zinc-400">
                      {Object.values(graderAnswers).filter(a => a && (typeof a === 'string' ? a.trim() : true)).length} of {totalQuestions} answered
                    </div>
                    <Button
                      onClick={submitGraderAnswers}
                      disabled={Object.values(graderAnswers).filter(a => a && (typeof a === 'string' ? a.trim() : true)).length < totalQuestions}
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      data-testid="submit-grading-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Submit for Grading
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
          
          {/* GRADER RESULTS */}
          {assessmentMode === 'grader' && completed && graderResults && (
            <div className="space-y-6">
              {/* Result Banner */}
              <div className={`p-6 rounded-xl border ${passed ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <div className="flex items-center gap-4">
                  {passed ? (
                    <Trophy className="h-12 w-12 text-green-400" />
                  ) : (
                    <AlertCircle className="h-12 w-12 text-red-400" />
                  )}
                  <div>
                    <h3 className={`text-xl font-bold ${passed ? 'text-green-400' : 'text-red-400'}`}>
                      {passed ? 'COMPETENT' : 'NOT YET COMPETENT'}
                    </h3>
                    <p className="text-zinc-400">
                      {passed 
                        ? 'Congratulations! You have demonstrated understanding of the learning objectives.'
                        : 'Review the course material and try again when ready.'
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Summary */}
              {graderResults.summary && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Brain className="h-5 w-5 text-[#1EBFB3]" />
                    Assessment Summary
                  </h4>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <p className="text-zinc-300 whitespace-pre-wrap">{graderResults.summary}</p>
                  </div>
                </div>
              )}
              
              {/* Score Breakdown */}
              {graderResults.score !== undefined && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-3">Score</h4>
                  <div className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-navigate-gold">
                      {Math.round(graderResults.score)}%
                    </div>
                    <Progress value={graderResults.score} className="flex-1 h-3" />
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Passing score: 70%
                  </p>
                </div>
              )}
              
              {/* Questions & Correct Answers Review */}
              {graderQuestions && graderQuestions.length > 0 && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#1EBFB3]" />
                    Review Questions & Correct Answers
                  </h4>
                  <div className="space-y-4">
                    {graderQuestions.map((q, idx) => {
                      const userAnswer = graderAnswers[idx];
                      const isCorrect = userAnswer === q.correctAnswer;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border ${
                            isCorrect 
                              ? 'bg-green-500/5 border-green-500/30' 
                              : 'bg-red-500/5 border-red-500/30'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                              isCorrect ? 'bg-green-500/20' : 'bg-red-500/20'
                            }`}>
                              {isCorrect ? (
                                <CheckCircle2 className="h-4 w-4 text-green-400" />
                              ) : (
                                <X className="h-4 w-4 text-red-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium mb-2">
                                Q{idx + 1}. {q.question}
                              </p>
                              
                              {/* Options with highlighting */}
                              <div className="space-y-1.5 mb-3">
                                {q.options?.map((option, optIdx) => {
                                  const isUserAnswer = option === userAnswer;
                                  const isCorrectAnswer = option === q.correctAnswer;
                                  
                                  return (
                                    <div 
                                      key={optIdx}
                                      className={`text-sm px-3 py-1.5 rounded ${
                                        isCorrectAnswer 
                                          ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                                          : isUserAnswer && !isCorrect
                                            ? 'bg-red-500/20 text-red-300 border border-red-500/30 line-through'
                                            : 'text-zinc-400'
                                      }`}
                                    >
                                      {String.fromCharCode(65 + optIdx)}. {option}
                                      {isCorrectAnswer && (
                                        <span className="ml-2 text-xs text-green-400">(Correct Answer)</span>
                                      )}
                                      {isUserAnswer && !isCorrect && (
                                        <span className="ml-2 text-xs text-red-400">(Your Answer)</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* Your Answer vs Correct Answer Summary */}
                              <div className="text-xs space-y-1 border-t border-zinc-700 pt-2 mt-2">
                                <p className={isCorrect ? 'text-green-400' : 'text-red-400'}>
                                  Your answer: {userAnswer || 'Not answered'}
                                </p>
                                {!isCorrect && (
                                  <p className="text-green-400">
                                    Correct answer: {q.correctAnswer}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* COACH MODE UI (Original Messages) */}
          {assessmentMode === 'coach' && (
            <>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-navigate-gold mx-auto mb-3" />
                    <p className="text-zinc-400">Starting your assessment...</p>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`${isFullscreen ? 'max-w-[70%]' : 'max-w-[85%]'} ${msg.role === 'user' ? 'order-1' : 'order-2'}`}>
                        {msg.role === 'navi' && (
                          <div className="flex items-center gap-2 mb-1">
                            <img 
                              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png" 
                              alt="NAVI" 
                              className="w-6 h-6 rounded-full object-cover"
                            />
                            <span className="text-xs text-[#1EBFB3] font-medium">NAVI</span>
                          </div>
                        )}
                        <div className={`p-4 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-navigate-gold text-black rounded-br-md'
                            : 'bg-zinc-800 text-zinc-100 rounded-bl-md'
                        }`}>
                          <MessageContent content={msg.content} isNavi={msg.role === 'navi'} highlightQuestions={true} />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-zinc-800 p-4 rounded-2xl rounded-bl-md">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                          <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </>
              )}
            </>
          )}
        </div>

        {/* Completion Panel - for Coach Mode */}
        {assessmentMode === 'coach' && completed && (
          <div className={`mx-4 mb-4 p-4 rounded-xl border ${
            passed 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {passed ? (
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-400" />
                )}
                <span className={`font-semibold ${passed ? 'text-green-400' : 'text-amber-400'}`}>
                  {passed ? 'Assessment Passed - Competent!' : 'Not Yet Competent'}
                </span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={downloadPdf}
                disabled={downloadingPdf}
                className="border-zinc-600"
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download Summary
              </Button>
              
              {!passed && (
                <Button
                  size="sm"
                  onClick={retakeAssessment}
                  className="bg-amber-600 text-white hover:bg-amber-500"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retake Assessment
                </Button>
              )}
            </div>
            
            {/* Close & Complete Button */}
            <div className="mt-4 pt-3 border-t border-zinc-700">
              <Button
                onClick={closeAndComplete}
                disabled={completingCourse}
                className="w-full bg-green-600 hover:bg-green-500 text-white"
                data-testid="close-complete-btn"
              >
                {completingCourse ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Close & Complete Course
              </Button>
              <p className="text-xs text-zinc-500 text-center mt-2">
                {passed 
                  ? 'XP points will be added to your profile!' 
                  : 'This course will appear in "Retake These Courses"'
                }
              </p>
            </div>
            
            {!passed && (
              <p className="text-sm text-zinc-400 mt-3">
                We recommend reviewing the course material before retaking the assessment.
              </p>
            )}
          </div>
        )}
        
        {/* Completion Panel - for Grader Mode */}
        {assessmentMode === 'grader' && completed && (
          <div className="mx-4 mb-4">
            <div className="flex flex-wrap gap-2 mt-3">
              {!passed && (
                <Button
                  size="sm"
                  onClick={retakeAssessment}
                  className="bg-amber-600 text-white hover:bg-amber-500"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retake Assessment
                </Button>
              )}
            </div>
            
            {/* Close & Complete Button */}
            <div className="mt-4 pt-3 border-t border-zinc-700">
              <Button
                onClick={closeAndComplete}
                disabled={completingCourse}
                className="w-full bg-green-600 hover:bg-green-500 text-white"
                data-testid="close-complete-btn-grader"
              >
                {completingCourse ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Close & Complete Course
              </Button>
              <p className="text-xs text-zinc-500 text-center mt-2">
                {passed 
                  ? 'XP points will be added to your profile!' 
                  : 'This course will appear in "Retake These Courses"'
                }
              </p>
            </div>
          </div>
        )}

        {/* Input Area - Only for Coach Mode */}
        {assessmentMode === 'coach' && !completed && (
          <div className={`p-4 border-t border-zinc-700 ${isFullscreen ? 'max-w-4xl mx-auto w-full' : ''}`}>
            <div className="flex gap-3">
              <Textarea
                ref={inputRef}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={phase === 'intro' ? "Type 'ready' to begin..." : "Type your response..."}
                className={`flex-1 ${isFullscreen ? 'min-h-[80px] max-h-48' : 'min-h-[60px] max-h-32'} bg-zinc-800 border-zinc-600 text-white resize-none`}
                disabled={sending || loading}
                data-testid="assessment-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!userInput.trim() || sending || loading}
                className="self-end bg-navigate-gold text-black hover:bg-navigate-gold-light px-4"
                data-testid="send-assessment-btn"
              >
                {sending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
            
            {totalQuestions > 0 && phase === 'questions' && (
              <p className="text-xs text-zinc-500 mt-2">
                Question {currentQuestion} of {totalQuestions} • Press Enter to send
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
